import type { ButtonHTMLAttributes, ReactNode } from "react";

/**
 * Buttons are rectangles with a 1px edge — no radius above the hairline token, no
 * shadow, no gradient. The variants differ by weight and by which of the two
 * colours they use, which is the whole palette this component is allowed.
 *
 * States are designed, not defaulted (`design-quality.md`): hover shifts the
 * fill rather than fading opacity, active nudges 1px down so a press is felt,
 * and disabled loses the edge as well as the fill so it does not read as
 * pressable.
 */

type Variant = "primary" | "secondary" | "ghost" | "danger";

const VARIANTS: Readonly<Record<Variant, string>> = {
  primary: "bg-ink text-paper border-ink hover:bg-signal hover:border-signal active:translate-y-px",
  secondary:
    "bg-paper-raised text-ink border-rule hover:border-ink hover:bg-paper-sunken active:translate-y-px",
  ghost: "bg-transparent text-ink-muted border-transparent hover:text-ink hover:border-rule",
  danger:
    "bg-transparent text-signal border-signal/40 hover:bg-signal hover:text-paper hover:border-signal active:translate-y-px",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: Variant;
  readonly children: ReactNode;
  /** Renders the pending label and blocks the press without losing layout width. */
  readonly pending?: boolean;
}

export function Button({
  variant = "secondary",
  children,
  pending = false,
  className = "",
  disabled,
  type = "button",
  ...rest
}: ButtonProps) {
  const isDisabled = disabled === true || pending;

  return (
    <button
      {...rest}
      type={type}
      disabled={isDisabled}
      // Announces the wait to a screen reader, which a spinner glyph does not.
      aria-busy={pending}
      className={[
        "inline-flex items-center justify-center gap-2 border px-3 py-1.5",
        "rounded-hairline text-micro font-semibold uppercase",
        "transition-colors duration-fast ease-swiss",
        "disabled:cursor-not-allowed disabled:border-rule disabled:bg-transparent disabled:text-ink-faint",
        VARIANTS[variant],
        className,
      ].join(" ")}
    >
      {pending ? <PendingMark /> : null}
      {children}
    </button>
  );
}

/**
 * A 1px bar that scans left to right. Chosen over a spinning ring because it
 * matches the rule vocabulary the rest of the interface is built from — and
 * because `prefers-reduced-motion` leaves it as a static bar, which still reads
 * as "busy" rather than as a frozen spinner.
 */
function PendingMark() {
  return (
    <span aria-hidden="true" className="relative block h-px w-3 overflow-hidden bg-current/30">
      <span className="rule-draw absolute inset-0 block bg-current" />
    </span>
  );
}
